import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  BellOff,
  CheckCheck,
  CheckCircle2,
  ClipboardList,
  Loader2,
  Megaphone,
  Pause,
  Play,
  ShieldCheck,
  Trash2,
  UserPlus,
  X,
  XCircle,
} from "lucide-react";
import {
  fetchNotifications,
  deleteNotification,
  clearNotifications,
  markAllNotificationsRead,
} from "../apis/notifications";
import { fetchTasks } from "../apis/tasks";
import useAutoRefresh from "../hooks/useAutoRefresh";
import { notifyBadgeRefresh } from "../utils/notificationEvents";
import { displayToast } from "../utils/Toast";
import ConfirmationModal from "./ConfirmationModal";
import "../styles/components/NotificationsPanel.css";

const EVENT_STYLES = [
  { match: ["circular"], icon: Megaphone, tone: "pink" },
  { match: ["approved", "approval"], icon: ShieldCheck, tone: "purple" },
  { match: ["completed", "completion"], icon: CheckCircle2, tone: "success" },
  { match: ["hold", "paused"], icon: Pause, tone: "warning" },
  { match: ["resumed", "active"], icon: Play, tone: "teal" },
  {
    match: ["reminder", "auto", "deadline", "approaching"],
    icon: UserPlus,
    tone: "cyan",
  },
  {
    match: ["rejected", "deleted", "cancelled"],
    icon: XCircle,
    tone: "danger",
  },
  { match: ["assigned", "created"], icon: UserPlus, tone: "info" },
];

const eventStyle = (type) => {
  const value = String(type || "").toLowerCase();
  const found = EVENT_STYLES.find((style) =>
    style.match.some((token) => value.includes(token)),
  );
  return found || { icon: ClipboardList, tone: "info" };
};

const splitDescription = (description) => {
  const text = String(description || "").trim();
  const index = text.lastIndexOf(":");
  if (index <= 0 || index === text.length - 1) {
    return { message: text, subject: "" };
  }
  return {
    message: text.slice(0, index).trim(),
    subject: text.slice(index + 1).trim(),
  };
};

const formatRelativeTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const diffSeconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diffSeconds < 60) return "Just now";
  const minutes = Math.floor(diffSeconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days > 1 ? "s" : ""} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months > 1 ? "s" : ""} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years > 1 ? "s" : ""} ago`;
};

const pick = (source, ...keys) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

const normalize = (item) => ({
  id: pick(item, "id", "ID", "Id"),
  type: pick(item, "type", "Type"),
  title: pick(item, "title", "Title"),
  description: pick(item, "description", "Description"),
  referenceType: pick(item, "reference_type", "referenceType", "ReferenceType"),
  referenceId: pick(item, "reference_id", "referenceId", "ReferenceId"),
  source: pick(item, "source", "Source"),
  actorName: pick(
    item,
    "actor_name",
    "actorName",
    "ActorName",
    "from_name",
    "fromName",
    "FromName",
    "sender_name",
    "senderName",
    "SenderName",
    "created_by_name",
    "createdByName",
    "CreatedByName",
    "triggered_by_name",
    "triggeredByName",
    "TriggeredByName",
  ),
  isImportant: Boolean(
    pick(item, "is_important", "isImportant", "IsImportant"),
  ),
  isRead: Boolean(pick(item, "is_read", "isRead", "IsRead", "read_at")),
  createdAt: pick(item, "created_at", "createdAt", "CreatedAt"),
});

const prettifyType = (value) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const initialsOf = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const ASSIGNER_EVENTS = [
  "completed",
  "completion",
  "approval_pending",
  "awaiting",
];

const resolveTarget = (notification) => {
  const reference = String(
    notification.referenceType || notification.type || "",
  ).toLowerCase();

  if (reference.includes("circular")) {
    return {
      path: "/tasks",
      state: { section: "circulars", circularId: notification.referenceId },
    };
  }

  if (!reference.includes("task")) return null;

  const type = String(notification.type || "").toLowerCase();
  const isAssignerEvent = ASSIGNER_EVENTS.some((event) => type.includes(event));

  return {
    path: "/tasks",
    state: {
      view: isAssignerEvent ? "by_me" : "for_me",
      status: "all",
      taskId: notification.referenceId,
    },
  };
};

const TASK_PAGE_SIZE = 100;

const enrichWithTaskPeople = async (items) => {
  const targets = items.map((item) => ({ item, target: resolveTarget(item) }));
  if (
    !targets.some(({ item, target }) => target?.state?.view && !item.actorName)
  ) {
    return items;
  }

  const [byMe, forMe] = await Promise.all([
    fetchTasks({ view: "by_me", page: 1, count: TASK_PAGE_SIZE }),
    fetchTasks({ view: "for_me", page: 1, count: TASK_PAGE_SIZE }),
  ]);

  const tasksByView = new Map();
  const collect = (res, view) => {
    const list = Array.isArray(res?.items) ? res.items : [];
    list.forEach((task) => tasksByView.set(`${view}:${task.id}`, task));
  };
  collect(byMe, "by_me");
  collect(forMe, "for_me");

  return targets.map(({ item, target }) => {
    if (item.actorName || !target?.state?.view) return item;
    const task = tasksByView.get(`${target.state.view}:${item.referenceId}`);
    if (!task) return item;
    const actorName =
      target.state.view === "by_me" ? task.assignee_name : task.assigner_name;
    return {
      ...item,
      actorName: actorName || item.actorName,
      taskTitle: task.title || "",
    };
  });
};

const NotificationsPanel = ({ onClose, anchorRef }) => {
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const loadNotifications = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetchNotifications();
      if (res?.error) {
        if (!silent) {
          displayToast(
            res?.error?.description || "Failed to load notifications",
            "error",
          );
        }
        return;
      }
      const list = Array.isArray(res) ? res : res?.items;
      const normalized = Array.isArray(list) ? list.map(normalize) : [];
      setNotifications(normalized);
      if (normalized.length) {
        try {
          setNotifications(await enrichWithTaskPeople(normalized));
        } catch (e) {
        }
      }
    } catch (e) {
      if (!silent) displayToast("Failed to load notifications", "error");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useAutoRefresh(() => loadNotifications(true));

  useEffect(() => {
    const onPointerDown = (event) => {
      if (panelRef.current?.contains(event.target)) return;
      if (anchorRef?.current?.contains(event.target)) return;
      onClose();
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [anchorRef, onClose]);

  const removeLocally = useCallback((id) => {
    setNotifications((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const unreadCount = useMemo(
    () => notifications.filter((item) => !item.isRead).length,
    [notifications],
  );

  const handleOpen = useCallback(
    (notification) => {
      removeLocally(notification.id);
      deleteNotification(notification.id)
        .catch(() => {})
        .finally(notifyBadgeRefresh);
      const target = resolveTarget(notification);
      if (target) navigate(target.path, { state: target.state });
      onClose();
    },
    [navigate, onClose, removeLocally],
  );

  const handleDelete = useCallback(
    async (id) => {
      if (busyId) return;
      setBusyId(id);
      try {
        const res = await deleteNotification(id);
        if (res?.error) {
          displayToast(
            res?.error?.description || "Failed to delete notification",
            "error",
          );
          return;
        }
        removeLocally(id);
        notifyBadgeRefresh();
      } catch (e) {
        displayToast("Failed to delete notification", "error");
      } finally {
        setBusyId(null);
      }
    },
    [busyId, removeLocally],
  );

  const handleClearAll = useCallback(async () => {
    setShowClearConfirm(false);
    try {
      const res = await clearNotifications();
      if (res?.error) {
        displayToast(
          res?.error?.description || "Failed to clear notifications",
          "error",
        );
        return;
      }
      setNotifications([]);
      notifyBadgeRefresh();
    } catch (e) {
      displayToast("Failed to clear notifications", "error");
    }
  }, []);

  const handleMarkAllRead = useCallback(async () => {
    try {
      const res = await markAllNotificationsRead();
      if (res?.error) {
        displayToast(
          res?.error?.description || "Failed to mark notifications as read",
          "error",
        );
        return;
      }
      setNotifications((prev) =>
        prev.map((item) => ({ ...item, isRead: true })),
      );
      notifyBadgeRefresh();
    } catch (e) {
      displayToast("Failed to mark notifications as read", "error");
    }
  }, []);

  return (
    <div className="notifications-panel" ref={panelRef} role="dialog">
      <div className="notifications-panel-head">
        <div className="notifications-panel-title">
          Notifications
          {unreadCount > 0 && (
            <span className="notifications-panel-badge">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </div>
        <div className="notifications-panel-head-actions">
          <button
            type="button"
            className="notifications-panel-action"
            title="Mark all as read"
            onClick={handleMarkAllRead}
            disabled={unreadCount === 0}
          >
            <CheckCheck size={15} />
          </button>
          <button
            type="button"
            className="notifications-panel-action"
            title="Clear all"
            onClick={() => setShowClearConfirm(true)}
            disabled={notifications.length === 0}
          >
            <Trash2 size={15} />
          </button>
          <button
            type="button"
            className="notifications-panel-action"
            title="Close"
            onClick={onClose}
          >
            <X size={15} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="notifications-loading">
          <Loader2 size={18} className="notifications-spin" />
          Loading notifications...
        </div>
      ) : notifications.length === 0 ? (
        <div className="notifications-empty">
          <BellOff size={26} />
          <h4>No notifications</h4>
          <p>You are all caught up.</p>
        </div>
      ) : (
        <div className="notifications-list">
          {notifications.map((notification) => {
            const { icon: Icon, tone } = eventStyle(notification.type);
            const { message, subject } = splitDescription(
              notification.description,
            );
            const taskName = notification.taskTitle || subject;
            const isBusy = busyId === notification.id;
            return (
              <div
                key={notification.id}
                className={`notification-card ${
                  notification.isRead ? "" : "unread"
                } ${notification.isImportant ? "important" : ""} ${
                  isBusy ? "busy" : ""
                }`}
                role="button"
                tabIndex={0}
                onClick={() => handleOpen(notification)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleOpen(notification);
                  }
                }}
              >
                <div className={`notification-icon ${tone}`}>
                  <Icon size={16} />
                </div>

                <div className="notification-body">
                  <div className="notification-title-row">
                    <span className={`notification-chip ${tone}`}>
                      {prettifyType(notification.type)}
                    </span>
                    <span className="notification-time">
                      {formatRelativeTime(notification.createdAt)}
                    </span>
                  </div>

                  <span className="notification-title">
                    {!notification.isRead && (
                      <span className="notification-dot" />
                    )}
                    {notification.title}
                  </span>

                  {message && (
                    <span
                      className="notification-message"
                      title={notification.description}
                    >
                      {message}
                    </span>
                  )}

                  {(taskName || notification.actorName) && (
                    <div className="notification-meta">
                      {notification.actorName && (
                        <span className="notification-actor">
                          <span className="notification-avatar">
                            {initialsOf(notification.actorName)}
                          </span>
                          {notification.actorName}
                        </span>
                      )}
                      {taskName && (
                        <span className="notification-tag" title={taskName}>
                          <ClipboardList size={12} />
                          {taskName}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  className="notification-icon-action"
                  title="Delete"
                  disabled={isBusy}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDelete(notification.id);
                  }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmationModal
        isOpen={showClearConfirm}
        message="Clear all notifications? This cannot be undone."
        confirmText="Clear all"
        cancelText="Cancel"
        onConfirm={handleClearAll}
        onClose={() => setShowClearConfirm(false)}
      />
    </div>
  );
};

export default NotificationsPanel;
